import { UISchemaElement } from '@jsonforms/core';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FieldErrors, FormProvider } from 'react-hook-form';
import { ajvResolver } from '~/components/JSONForm/ajvResolver';
import { JSONFormAdapter } from '~/components/JSONForm/JsonFormAdapter';
import { useCustomForm } from '~/hooks/useCustomForm';
import { JSONFormData, PeerReference } from '~/types';
import { WizardProps } from '../constants';
import { WizardFooter } from './WizardFooter';

const TAGS = CT.peerRef.form;

// Discriminate leaf FieldError vs nested FieldErrors container by the required
// `type: string` discriminant (e.g. "required", "min"). Key-presence checks
// false-positive on JSON Forms whose fields are literally named `type`,
// `message`, etc. — the outer container would have those keys but their
// values would be nested error objects, not strings.
const isFieldErrorNode = (value: unknown): value is { type: string; message?: unknown } => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record.type === 'string';
};

export const findFirstJsonFormErrorPath = (
  errors: FieldErrors | Record<string, unknown>,
  parentPath = ''
): string | undefined => {
  for (const [key, value] of Object.entries(errors)) {
    if (!value) continue;
    const path = parentPath ? `${parentPath}.${key}` : key;
    if (isFieldErrorNode(value)) return path;
    if (typeof value === 'object') {
      const nestedPath = findFirstJsonFormErrorPath(value as Record<string, unknown>, path);
      if (nestedPath) return nestedPath;
    }
  }
  return undefined;
};

const fieldNameSelector = (name: string) =>
  `[name="${name.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;

const fieldFallbackSelector = (name: string) =>
  `[data-json-form-field="${name.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;

export const focusFirstJsonFormError = (errors: FieldErrors | Record<string, unknown>) => {
  const firstErrorPath = findFirstJsonFormErrorPath(errors);
  if (!firstErrorPath) return;

  const field = document.querySelector<HTMLElement>(
    `${fieldNameSelector(firstErrorPath)}, ${fieldFallbackSelector(firstErrorPath)}`
  );
  field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  field?.focus({ preventScroll: true });
};

export const JSONFormWrapper = (props: WizardProps) => {
  const { actor } = props;
  const { form, formUi, defaultJSONFormData, peerReference } = useSelector(
    actor,
    (state) => state.context
  );

  const methods = useCustomForm({
    resolver: ajvResolver(form),
    defaultValues: defaultJSONFormData || {},
  });

  const onPageSubmit = (values: JSONFormData) => {
    const partialPeerRef: Partial<PeerReference> = {
      formInstance: {
        id: peerReference.formInstanceId,
        formType: peerReference.formInstance?.formType || 'peer_reference',
        formData: values,
        jsonFormId: peerReference.jsonForm.id,
      },
    };
    actor.send({ type: 'ON_WIZ_SUBMIT', values: partialPeerRef });
  };

  const nextBtn = {
    text: 'Submit',
    onClick: async () => {
      methods.handleSubmit(onPageSubmit, focusFirstJsonFormError)();
    },
  };

  const prevBtn = {
    shouldShow: true,
    onClick: () => {
      actor.send({
        type: 'ON_WIZ_PREV_PAGE',
        draft: { kind: 'json', values: methods.getValues() },
      });
    },
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          data-cy={TAGS.jsonForm}
          className="max-h-[55vh] overflow-y-auto pr-1 text-left leading-relaxed"
        >
          <JSONFormAdapter
            formDataSchema={form}
            layoutSchema={formUi as UISchemaElement}
            defaultValues={defaultJSONFormData}
          />
        </form>
      </FormProvider>
      <WizardFooter nextBtn={nextBtn} prevBtn={prevBtn} />
    </>
  );
};
